import {
  ColumnDefinition,
  DataTable,
  ZeroState,
  useToast,
} from "@codat/orchard-ui";
import { DataConnection } from "api/public";
import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { ConnectionStatusPill } from "components/ConnectionStatusPill";
import DeleteConnectionModal from "components/DeleteConnectionModal/DeleteConnectionModal";
import GenericCell from "components/GenericCell";
import UnlinkConfirmationModal from "components/UnlinkConfirmationModal";

import ActionsCell from "./cells/ActionsCell";
import { ConnectionIcon } from "./ConnectionIcon";
import styles from "./ConnectionsTable.module.scss";

interface ConnectionsTableProps {
  dataConnections: DataConnection[];
  companyName: string;
  isSkeleton: boolean;
  onRefetchConnections: () => void;
}

export const getColumnDefinitions = (): ColumnDefinition<
  "connection" | "sourceType" | "status" | "actions"
>[] => {
  return [
    {
      key: "connection",
      label: "Connection",
      component: GenericCell,
      className: styles.connectionCell,
      colWidth: 50,
    },
    {
      key: "sourceType",
      label: "Source type",
      component: GenericCell,
      className: styles.genericCell,
    },
    {
      key: "status",
      label: "Status",
      component: ({ value }) => <ConnectionStatusPill status={value.status} />,
      className: styles.genericCell,
    },
    {
      key: "actions",
      label: "",
      component: ActionsCell,
      className: styles.genericCell,
      align: "right",
    },
  ];
};

export const getTableRows = ({
  dataConnections,
  onCopyConnectionId,
  onUnlink,
  onDelete,
}: {
  dataConnections: DataConnection[];
  onCopyConnectionId: () => void;
  onUnlink: (connection: DataConnection) => void;
  onDelete: (connection: DataConnection) => void;
}) => {
  return dataConnections.map((dataConnection, i) => {
    return {
      data: [
        {
          key: "platformName",
          value: {
            content: dataConnection.platformName,
            testId: `connections-table-platform-name-${i}`,
            iconLeft: <ConnectionIcon sourceType={dataConnection.sourceType} />,
            subtext: dataConnection.id,
            onCopySubtext: onCopyConnectionId,
          },
        },
        {
          key: "sourceType",
          value: {
            content: dataConnection.sourceType,
            testId: `connections-table-source-type-${i}`,
          },
        },
        {
          key: "status",
          value: {
            status: dataConnection.status,
          },
        },
        {
          key: "actions",
          value: {
            platformName: dataConnection.platformName,
            linkUrl: dataConnection.linkUrl,
            integrationId: dataConnection.integrationId,
            connectionStatus: dataConnection.status,
            onUnlink: () => onUnlink(dataConnection),
            onDelete: () => onDelete(dataConnection),
          },
        },
      ],
    };
  });
};

export const ConnectionsTable: React.FC<ConnectionsTableProps> = ({
  dataConnections,
  companyName,
  onRefetchConnections,
  isSkeleton,
}) => {
  const { companyId } = useParams<{ companyId: string }>();
  const { addToast } = useToast();
  const [connectionToUnlink, setConnectionToUnlink] =
    useState<DataConnection | null>(null);
  const [connectionToDelete, setConnectionToDelete] =
    useState<DataConnection | null>(null);

  const onCopyConnectionId = () => {
    addToast({
      message: "Copied connection ID to clipboard",
      variant: "success",
    });
  };
  if (!isSkeleton && dataConnections.length === 0) {
    return (
      <ZeroState
        title="No connections"
        description={`No connections exist for ${companyName}. Once you add connections, they will appear here.`}
      />
    );
  }
  return (
    <>
      <DataTable
        rowsInfo={getTableRows({
          dataConnections,
          onCopyConnectionId,
          onUnlink: setConnectionToUnlink,
          onDelete: setConnectionToDelete,
        })}
        columnDefs={getColumnDefinitions()}
        hasBorder
        isSkeleton={isSkeleton}
        data-testid="connections-table"
      />
      {connectionToUnlink && (
        <UnlinkConfirmationModal
          companyId={companyId}
          connection={connectionToUnlink}
          onCancel={() => setConnectionToUnlink(null)}
          onSuccess={() => {
            onRefetchConnections();
            setConnectionToUnlink(null);
          }}
        />
      )}
      {connectionToDelete && (
        <DeleteConnectionModal
          companyId={companyId}
          connection={connectionToDelete}
          onCancel={() => setConnectionToDelete(null)}
          onSuccess={() => {
            onRefetchConnections();
            setConnectionToDelete(null);
          }}
        />
      )}
    </>
  );
};
